import { ConfigureApplicantStageChangeUpdateDetailsBaseProps } from 'apps/sigma2-ui/src/components/workflows/types';
import React, { useCallback } from 'react';
import { S2Checkbox } from 'apps/sigma2-ui/src/components/ui/Checkbox';
import { WorkflowWriteBackMode } from 'libs/end-to-end/src/types/databases/workflows/workflow-write-back-mode';
import { ConfigureWriteBackMode } from 'apps/sigma2-ui/src/components/workflows/ConfigureWriteBackMode';
import { WorkflowApplicantStageChangeUpdateDetails } from 'libs/end-to-end/src/types/databases/workflows/applicant-stage-change-update/workflow-applicant-stage-change-update';
import { WriteBackToFountainAuthMethod } from 'libs/end-to-end/src/types/databases/workflows/strategies/fountain-write-back';
import { WriteBackToFountainStrategy } from 'libs/end-to-end/src/types/databases/workflows/strategies/fountain-write-back';
import { ConfigureWriteBackFountainAuthMethod } from 'apps/sigma2-ui/src/components/workflows/ConfigureWriteBackFountainAuthMethod';
import { S2Typography } from 'apps/sigma2-ui/src/components/ui/Typography';
import { Button } from 'apps/sigma2-ui/src/components/ui/Button';
import { Table } from 'apps/sigma2-ui/src/components/ui/Table';
import { SelectiveFilterStringBasedOptionComponent } from 'apps/sigma2-ui/src/SelectiveFilteringOptionComponent';
import { useCrudOperations } from 'apps/sigma2-ui/src/data-wizard/components/composite-dataset/recipes/use-crud-operations';

export interface ConfigureApplicantStageChangeUpdateDetailsProps
  extends ConfigureApplicantStageChangeUpdateDetailsBaseProps {
  details: WorkflowApplicantStageChangeUpdateDetails | null;
  updateDetails: (
    details: WorkflowApplicantStageChangeUpdateDetails | null,
  ) => void;
}

export const ConfigureApplicantStageChangeUpdateDetails = ({
  details,
  updateDetails,
  listDataSetsPattern,
  listSecretsPattern,
  organizationId,
}: ConfigureApplicantStageChangeUpdateDetailsProps) => {
  const updateDetailsPartial = useCallback(
    (partial: Partial<WorkflowApplicantStageChangeUpdateDetails>) => {
      if (!details) {
        return;
      }
      updateDetails({
        ...details,
        ...partial,
      });
    },
    [details, updateDetails],
  );

  const toggleWriteBackEnabled = useCallback(() => {
    updateDetails(
      details
        ? null
        : {
            type: 'applicantStageChangeUpdateDetails',
            strategy: {
              type: 'fountain',
            },
            stageChangeRules: [],
            isNewFountainStageMapOn: false,
          },
    );
  }, [details, updateDetails]);

  const updateMode = useCallback(
    (mode: WorkflowWriteBackMode) => {
      updateDetailsPartial({
        mode,
      });
    },
    [updateDetailsPartial],
  );

  const updateStrategy = useCallback(
    (strategy: WriteBackToFountainStrategy | undefined) => {
      updateDetailsPartial({
        strategy: strategy,
      });
    },
    [updateDetailsPartial],
  );

  const updateMinReadinessRating = useCallback(
    (minReadinessRating: number | undefined) => {
      updateDetailsPartial({
        minReadinessRating:
          minReadinessRating !== undefined && isNaN(minReadinessRating)
            ? undefined
            : minReadinessRating,
      });
    },
    [updateDetailsPartial],
  );

  const updateNumberOfSamplesToSend = useCallback(
    (numberOfSamplesToSend: number | undefined) => {
      updateDetailsPartial({
        numberOfSamplesToSend:
          numberOfSamplesToSend !== undefined && isNaN(numberOfSamplesToSend)
            ? undefined
            : numberOfSamplesToSend,
      });
    },
    [updateDetailsPartial],
  );

  const updateIsNewFountainStageMapOn = useCallback(
    (isNewFountainStageMapOn: boolean) => {
      updateDetailsPartial({
        isNewFountainStageMapOn,
      });
    },
    [updateDetailsPartial],
  );

  const updateAuthMethod = useCallback(
    (authMethod: WriteBackToFountainAuthMethod | null) => {
      updateStrategy({
        ...details?.strategy,
        type: 'fountain',
        authMethod: authMethod ?? undefined,
      });
    },
    [details?.strategy, updateStrategy],
  );

  const stageChangeOps = useCrudOperations({
    items: details?.stageChangeRules ?? [],
    setItems: useCallback(
      (
        items: WorkflowApplicantStageChangeUpdateDetails['stageChangeRules'],
      ) => {
        updateDetailsPartial({
          stageChangeRules: items,
        });
      },
      [updateDetailsPartial],
    ),
  });

  return (
    <div className="space-y-6 p-6 bg-white rounded-lg shadow-md">
      <h2 className="text-xl font-semibold">
        Configure Applicant Stage Change Update
      </h2>
      <S2Checkbox checked={Boolean(details)} onChange={toggleWriteBackEnabled}>
        Enable the applicant stage change updates
      </S2Checkbox>

      {details && (
        <>
          <ConfigureWriteBackMode
            mode={details.mode ?? null}
            updateMode={updateMode}
          />

          <ConfigureWriteBackFountainAuthMethod
            authMethod={details.strategy?.authMethod ?? null}
            updateAuthMethod={updateAuthMethod}
            listSecretsPattern={listSecretsPattern}
            organizationId={organizationId}
            listDataSetsPattern={listDataSetsPattern}
          />

          <S2Typography
            variant="headline-5-alt"
            translatedContent={'Stage Change Rules'}
          />

          <Button
            variant="primary"
            size="small"
            behavior={{
              type: 'onClick',
              onClick: () => {
                stageChangeOps.onAdd({
                  sourceStageMatch: { type: 'equals', value: '' },
                  targetStageMatch: { type: 'equals', value: '' },
                });
              },
            }}
          >
            Add
          </Button>

          <Table
            columnDefinitions={[
              {
                id: 'source',
                header: () => 'Source Stage Rule',
                cell: ({ item, rowIndex }) => (
                  <SelectiveFilterStringBasedOptionComponent
                    option={item.sourceStageMatch}
                    setOption={(option) => {
                      stageChangeOps.onEdit(rowIndex, {
                        sourceStageMatch: option,
                      });
                    }}
                  />
                ),
              },
              {
                id: 'target',
                header: () => 'Target Stage Rule',
                cell: ({ item, rowIndex }) => (
                  <SelectiveFilterStringBasedOptionComponent
                    option={item.targetStageMatch}
                    setOption={(option) => {
                      stageChangeOps.onEdit(rowIndex, {
                        targetStageMatch: option,
                      });
                    }}
                  />
                ),
              },
              {
                id: 'delete',
                header: () => 'Actions',
                cell: ({ rowIndex }) => (
                  <Button
                    variant="destructive"
                    size="small"
                    behavior={{
                      type: 'onClick',
                      onClick: () => {
                        stageChangeOps.onDelete(rowIndex);
                      },
                    }}
                  >
                    Delete
                  </Button>
                ),
              },
            ]}
            items={details.stageChangeRules ?? []}
            empty={'No stage change rules configured.'}
          />

          <label className="space-y-1 flex flex-col">
            <span>Minimum readiness rating</span>

            <input
              type="number"
              value={details.minReadinessRating ?? ''}
              onChange={(e) => {
                updateMinReadinessRating(e.target.valueAsNumber);
              }}
              className="form-input border rounded p-2"
            />
          </label>

          <label className="space-y-1 flex flex-col">
            <span>Number of Samples To Send</span>

            <input
              type="number"
              value={details.numberOfSamplesToSend ?? ''}
              onChange={(e) => {
                updateNumberOfSamplesToSend(e.target.valueAsNumber);
              }}
              className="form-input border rounded p-2"
            />
          </label>

          <S2Checkbox
            checked={details.isNewFountainStageMapOn ?? false}
            onChange={() => {
              updateIsNewFountainStageMapOn(!details.isNewFountainStageMapOn);
            }}
          >
            Enable New Fountain Stage Map
          </S2Checkbox>
        </>
      )}
    </div>
  );
};
